import { createLocalStorageStore } from 'features/entryBrowser/hooks/createLocalStorageStore'
import {
  getCantileverDocumentId,
  getSystemDocumentBqId,
  type SystemDocumentEntry,
} from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'

export interface FavoriteDocument {
  id: string
  title: string
  filename: string
  documentTitle: string | null
  timestamp: number
  /** Runtime-enriched from active Rolodex docs; not persisted as a dual-read source of truth. */
  bqDocumentId?: string
  /** Runtime-enriched from active Rolodex docs; not persisted as a dual-read source of truth. */
  cantileverDocumentId?: string
}

const MAX_FAVORITES = 100
const STORAGE_KEY_PREFIX = 'gecko_favorite_documents:v1'

function getStorageKey(userId: string, siteId: string): string {
  return `${STORAGE_KEY_PREFIX}_${userId}_${siteId}`
}

const storeCache = new Map<
  string,
  ReturnType<typeof createLocalStorageStore<FavoriteDocument>>
>()

export function getFavoritesStore(userId: string, siteId: string) {
  const key = getStorageKey(userId, siteId)
  let store = storeCache.get(key)
  if (!store) {
    store = createLocalStorageStore<FavoriteDocument>(key, MAX_FAVORITES)
    storeCache.set(key, store)
  }
  return store
}

export function resolveFavoritesForActiveDocuments({
  favoriteDocuments,
  activeDocuments,
}: {
  favoriteDocuments: FavoriteDocument[]
  activeDocuments: SystemDocumentEntry[]
}): FavoriteDocument[] {
  const activeDocumentById = new Map(
    activeDocuments.map((entry) => [entry.entry.id, entry])
  )

  return favoriteDocuments.flatMap((favorite) => {
    const activeDocument = activeDocumentById.get(favorite.id)
    if (!activeDocument) return []

    return [
      {
        ...favorite,
        title: activeDocument.entry.data.title,
        filename: activeDocument.entry.data.filename,
        documentTitle:
          activeDocument.entry.data.document_title ??
          activeDocument.entry.data.title ??
          null,
        bqDocumentId: getSystemDocumentBqId(activeDocument),
        cantileverDocumentId: getCantileverDocumentId(activeDocument),
      },
    ]
  })
}
